import { Button } from '@ordr-as/umami-react'
import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { formatDateAndTime } from 'utils/stringUtils'
import { FailedPspNotificationDto } from '../../../../generated-api'
import { useSelectable } from '../hooks/useSelectable'
import { usePaymentOperations, WatchedRowKind, WatchedRowStatus } from '../context/PaymentOperationsContext'
import { RefreshButton } from '../RefreshButton'
import CollapsiblePanel from '../CollapsiblePanel'

export const FailedPspNotificationsTable = ({
    failedPspNotifications,
}: {
    failedPspNotifications: FailedPspNotificationDto[]
}) => {
    const { retryFailed, isRetrying, watchedRows } = usePaymentOperations()

    const canRetry = (notification: FailedPspNotificationDto) => notification.retryCount < 5
    const { selected, isChecked, toggle, isAllChecked, toggleAll, isNonChecked, reset } = useSelectable(
        failedPspNotifications,
        {
            getId: (notification) => notification.pspNotificationId,
            isSelectable: canRetry,
        }
    )

    const retryAll = useCallback(() => {
        const notificationIds = selected
        retryFailed(notificationIds)
        reset()
    }, [reset, retryFailed, selected])

    const getRowStatusClass = (id: number) => {
        const status = watchedRows.find((row) => row.id === id && row.kind === WatchedRowKind.psp)?.status

        const defaultClass = 'hover:bg-purple-lightest border-t border-grey-lightest cursor-pointer '
        if (!status) {
            return defaultClass
        }

        return status === WatchedRowStatus.processing
            ? defaultClass + 'bg-brand-accent'
            : defaultClass + 'bg-green-lightest'
    }

    const retryButtonText = isAllChecked ? 'Retry all' : 'Retry selected'

    return (
        <CollapsiblePanel title={`Failed PSP Notifications (${failedPspNotifications.length})`} defaultOpen>
            <div>
                <div className="flex justify-end gap-4 px-5 pb-2">
                    <Button onClick={retryAll} isDisabled={isRetrying || isNonChecked} kind="secondary">
                        {retryButtonText}
                    </Button>
                    <RefreshButton />
                </div>
                <div className="overflow-x-auto">
                    <table className="text-left min-w-full">
                        <thead className="text-grey-dark">
                            <tr>
                                <th className="py-2 px-5">
                                    <input type="checkbox" checked={isAllChecked} onChange={toggleAll} />
                                </th>
                                <th className="font-semibold py-1 px-2">ID</th>
                                <th className="font-semibold py-1 px-2">Failure Reason</th>
                                <th className="font-semibold py-1 px-2 text-center">Retry Count</th>
                                <th className="font-semibold py-1 px-2">Created</th>
                                <th className="font-semibold py-1 px-2">Updated</th>
                                <th className="font-semibold py-1 px-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failedPspNotifications.length ? (
                                failedPspNotifications.map((notification) => (
                                    <tr
                                        key={notification.pspNotificationId}
                                        className={getRowStatusClass(notification.pspNotificationId)}
                                        onClick={() => canRetry(notification) && toggle(notification)}
                                    >
                                        <td className="py-2 px-2">
                                            <input
                                                type="checkbox"
                                                disabled={!canRetry(notification)}
                                                checked={isChecked(notification)}
                                                onChange={() => toggle(notification)}
                                            />
                                        </td>
                                        <td className="py-2 px-2 text-sm">{notification.pspNotificationId}</td>
                                        <td className="py-2 px-2 text-sm">{notification.latestFailureReason}</td>
                                        <td className="py-2 px-2 text-sm text-center">{notification.retryCount}</td>
                                        <td className="py-2 px-2 text-sm">{formatDateAndTime(notification.created)}</td>
                                        <td className="py-2 px-2 text-sm">{formatDateAndTime(notification.updated)}</td>
                                        <td className="py-2 px-2 text-sm">
                                            <Link
                                                to={`${notification.pspNotificationId}`}
                                                className="text-brand-primary mr-2"
                                            >
                                                Details
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="py-4 px-5 text-center">
                                        No failed notifications
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CollapsiblePanel>
    )
}
